import React, { useEffect, useMemo, useState } from 'react';

const PRESETS = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'month', label: 'This Month' },
  { value: 'year', label: 'This Year' },
  { value: '12m', label: '12 Months' },
];

const STORAGE_KEY = 'analytics-controls:';

const todayIso = () => new Date().toISOString().slice(0, 10);

const defaultVisibility = (chartKeys = []) => chartKeys.reduce((acc, key) => {
  acc[key] = true;
  return acc;
}, {});

export const useAnalyticsControls = ({
  storageKey,
  chartKeys = [],
  defaultGroupBy = 'month',
  defaultPreset = '12m',
} = {}) => {
  const chartKeySignature = useMemo(() => chartKeys.join('|'), [chartKeys]);
  const baseVisibility = useMemo(() => defaultVisibility(chartKeys), [chartKeySignature]);
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(`${STORAGE_KEY}${storageKey}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          preset: parsed.preset || defaultPreset,
          groupBy: parsed.groupBy || defaultGroupBy,
          from: parsed.from || '',
          to: parsed.to || '',
          chartVisibility: { ...baseVisibility, ...(parsed.chartVisibility || {}) },
        };
      }
    } catch (_) {}
    return {
      preset: defaultPreset,
      groupBy: defaultGroupBy,
      from: '',
      to: '',
      chartVisibility: baseVisibility,
    };
  });

  useEffect(() => {
    try {
      localStorage.setItem(`${STORAGE_KEY}${storageKey}`, JSON.stringify(state));
    } catch (_) {}
  }, [state, storageKey]);

  useEffect(() => {
    setState((current) => ({
      ...current,
      chartVisibility: Object.keys(baseVisibility).every((key) => current.chartVisibility?.[key] !== undefined)
        ? current.chartVisibility
        : { ...baseVisibility, ...current.chartVisibility },
    }));
  }, [chartKeySignature, baseVisibility]);

  const queryParams = useMemo(() => {
    const params = { preset: state.preset, groupBy: state.groupBy };
    if (state.from) params.from = state.from;
    if (state.to) params.to = state.to;
    return params;
  }, [state]);

  return {
    state,
    queryParams,
    setPreset: (preset) => setState((current) => ({ ...current, preset, ...(preset === 'custom' ? {} : { from: '', to: '' }) })),
    setGroupBy: (groupBy) => setState((current) => ({ ...current, groupBy })),
    setFrom: (from) => setState((current) => ({ ...current, from, preset: 'custom' })),
    setTo: (to) => setState((current) => ({ ...current, to, preset: 'custom' })),
    toggleChart: (key) => setState((current) => ({
      ...current,
      chartVisibility: { ...current.chartVisibility, [key]: !current.chartVisibility[key] },
    })),
    reset: () => setState({
      preset: defaultPreset,
      groupBy: defaultGroupBy,
      from: '',
      to: '',
      chartVisibility: defaultVisibility(chartKeys),
    }),
  };
};

export function AnalyticsToolbar({
  title,
  subtitle,
  state,
  meta,
  onPresetChange,
  onGroupByChange,
  onFromChange,
  onToChange,
  onOpenSettings,
  onOpenInfo,
  presets = PRESETS,
  showTitle = true,
  showGroupBy = true,
  showSettings = true,
  showInfo = true,
  dateInputLabels = false,
  settingsLabel = 'Charts',
  infoLabel = 'Info',
  wrapPresets = false,
}) {
  return (
    <div className="card border-0 shadow-sm mb-4">
      <div className="card-body py-3">
        <div className="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3">
          {showTitle ? (
            <div>
              <div className="fw-semibold text-dark">{title || 'Analytics Controls'}</div>
              <div className="small text-muted">{meta?.periodLabel || subtitle || 'Adjust the charts using date range and grouping.'}</div>
            </div>
          ) : (
            <div className="small text-muted">{meta?.periodLabel || subtitle || 'Adjust the charts using date range and grouping.'}</div>
          )}

          <div className="d-flex flex-wrap align-items-center gap-2">
            <div className={wrapPresets ? 'd-flex flex-wrap gap-1' : 'btn-group btn-group-sm'}>
              {presets.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  className={`btn ${wrapPresets ? 'btn-sm' : ''} ${state.preset === preset.value ? 'btn-primary' : 'btn-outline-primary'}`}
                  style={wrapPresets ? { whiteSpace: 'normal' } : undefined}
                  onClick={() => onPresetChange(preset.value)}
                >
                  {preset.label}
                </button>
              ))}
            </div>

            {showGroupBy && (
              <select className="form-select form-select-sm" style={{ width: 120 }} value={state.groupBy} onChange={(e) => onGroupByChange(e.target.value)}>
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </select>
            )}

            {dateInputLabels ? (
              <label className="small text-muted d-flex align-items-center gap-1 mb-0">
                From
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ width: 150 }}
                  max={state.to || todayIso()}
                  value={state.from || ''}
                  onChange={(e) => onFromChange(e.target.value)}
                />
              </label>
            ) : (
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 150 }}
                max={state.to || todayIso()}
                value={state.from || ''}
                onChange={(e) => onFromChange(e.target.value)}
              />
            )}
            {dateInputLabels ? (
              <label className="small text-muted d-flex align-items-center gap-1 mb-0">
                To
                <input
                  type="date"
                  className="form-control form-control-sm"
                  style={{ width: 150 }}
                  min={state.from || undefined}
                  max={todayIso()}
                  value={state.to || ''}
                  onChange={(e) => onToChange(e.target.value)}
                />
              </label>
            ) : (
              <input
                type="date"
                className="form-control form-control-sm"
                style={{ width: 150 }}
                min={state.from || undefined}
                max={todayIso()}
                value={state.to || ''}
                onChange={(e) => onToChange(e.target.value)}
              />
            )}

            {showSettings && (
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onOpenSettings}>
                <i className="bi bi-sliders me-1" />{settingsLabel}
              </button>
            )}
            {showInfo && (
              <button type="button" className="btn btn-outline-info btn-sm" onClick={onOpenInfo}>
                <i className="bi bi-info-circle me-1" />{infoLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AnalyticsSettingsModal({ show, title = 'Analytics Settings', charts = [], visibility = {}, onToggle, onClose, onReset }) {
  if (!show) return null;
  return (
    <div className="modal fade show d-block" style={{ background: 'rgba(15,23,42,0.45)', zIndex: 1050 }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="small text-muted mb-3">Turn chart sections on or off based on what management needs to see.</div>
            <div className="d-flex flex-column gap-2">
              {charts.map((chart) => (
                <label key={chart.key} className="d-flex align-items-center justify-content-between border rounded px-3 py-2">
                  <span>
                    <span className="fw-semibold d-block">{chart.label}</span>
                    {chart.help && <span className="small text-muted">{chart.help}</span>}
                  </span>
                  <div className="form-check form-switch mb-0">
                    <input className="form-check-input" type="checkbox" checked={visibility[chart.key] !== false} onChange={() => onToggle(chart.key)} />
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onReset}>Reset</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AnalyticsInfoModal({ show, title = 'Analytics Guide', lines = [], onClose }) {
  if (!show) return null;
  return (
    <div className="modal fade show d-block" style={{ background: 'rgba(15,23,42,0.45)', zIndex: 1050 }} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="d-flex flex-column gap-2">
              {lines.map((line, index) => (
                <div key={index} className="small text-muted">{line}</div>
              ))}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-primary btn-sm" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
